/**
 * CAB API Service
 *
 * REST API client for the Common Application Blueprint module.
 */

import api from '../utils/axios';
import type {
  CABFileUploadResponse,
  CABFileListResponse,
  CABFileInfoResponse,
  CABSessionResponse,
  CABProgressResponse,
  CABGenerateRequest,
} from '../types/cab';

const CAB_BASE = '/cab';

// ════════════════════════════════════════════════════════════════════════════
// FILE MANAGEMENT
// ════════════════════════════════════════════════════════════════════════════

/**
 * Upload a file for CAB processing.
 * Supports large files — streamed as multipart/form-data.
 */
export async function uploadCABFile(
  projectId: string,
  file: File,
  onProgress?: (percent: number) => void,
  abortController?: AbortController,
): Promise<CABFileUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<CABFileUploadResponse>(
    `${CAB_BASE}/${projectId}/files/upload`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: abortController?.signal,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    },
  );
  return response.data;
}

/**
 * List all uploaded CAB files for a project.
 */
export async function listCABFiles(projectId: string): Promise<CABFileListResponse> {
  const response = await api.get<CABFileListResponse>(
    `${CAB_BASE}/${projectId}/files`,
  );
  return response.data;
}

/**
 * Delete a CAB file.
 */
export async function deleteCABFile(projectId: string, fileId: string): Promise<void> {
  await api.delete(`${CAB_BASE}/${projectId}/files/${fileId}`);
}

/**
 * Download an uploaded CAB file.
 */
export async function downloadCABFile(projectId: string, fileId: string, fileName: string): Promise<void> {
  const response = await api.get(`${CAB_BASE}/${projectId}/files/${fileId}/download`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ════════════════════════════════════════════════════════════════════════════
// SESSION STATUS & PROGRESS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Lightweight check for CAB.md availability — used by DocumentsPanel / FileUploadDialog.
 */
export async function getCABFileInfo(projectId: string): Promise<CABFileInfoResponse> {
  const response = await api.get<CABFileInfoResponse>(
    `${CAB_BASE}/${projectId}/file-info`,
  );
  return response.data;
}

/**
 * Get CAB session status for a project.
 */
export async function getCABStatus(projectId: string): Promise<CABSessionResponse> {
  const response = await api.get<CABSessionResponse>(
    `${CAB_BASE}/${projectId}/status`,
  );
  return response.data;
}

/**
 * Get detailed CAB generation progress (for polling).
 */
export async function getCABProgress(projectId: string): Promise<CABProgressResponse> {
  const response = await api.get<CABProgressResponse>(
    `${CAB_BASE}/${projectId}/progress`,
  );
  return response.data;
}

// ════════════════════════════════════════════════════════════════════════════
// GENERATION CONTROL (placeholder — wired in Stage 2)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Start CAB generation.
 */
export async function generateCAB(
  projectId: string,
  config?: CABGenerateRequest,
): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/generate`, config || {});
}

/**
 * Pause CAB generation.
 */
export async function pauseCAB(projectId: string): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/pause`);
}

/**
 * Resume CAB generation from checkpoint.
 */
export async function resumeCAB(projectId: string): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/resume`);
}

/**
 * Cancel CAB generation.
 */
export async function cancelCAB(projectId: string): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/cancel`);
}

/**
 * Regenerate CAB from scratch.
 */
export async function regenerateCAB(
  projectId: string,
  config?: CABGenerateRequest,
): Promise<void> {
  await api.post(`${CAB_BASE}/${projectId}/regenerate`, config || {});
}

// ════════════════════════════════════════════════════════════════════════════
// OUTPUT ACCESS (placeholder — wired in Stage 4-5)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Get the skeleton JSON.
 */
export async function getCABSkeleton(projectId: string): Promise<any> {
  const response = await api.get(`${CAB_BASE}/${projectId}/skeleton`);
  return response.data;
}

/**
 * Get the final CAB JSON.
 */
export async function getCABJson(projectId: string): Promise<any> {
  const response = await api.get(`${CAB_BASE}/${projectId}/cab`);
  return response.data;
}

/**
 * Get the rendered HTML (runtime from CAB JSON + template).
 */
export async function getCABHtml(projectId: string, wip = false): Promise<string> {
  const response = await api.get(`${CAB_BASE}/${projectId}/html`, {
    params: { wip },
    responseType: 'text',
  });
  return response.data;
}

/**
 * Fetch the CAB markdown content as text (for preview).
 */
export async function getCABMarkdownContent(projectId: string): Promise<string> {
  const response = await api.get(`${CAB_BASE}/${projectId}/markdown`, {
    responseType: 'text',
  });
  return response.data;
}

/**
 * Download the CAB as a Markdown file.
 */
export async function downloadCABMarkdown(projectId: string): Promise<void> {
  const response = await api.get(`${CAB_BASE}/${projectId}/markdown`, {
    responseType: 'blob',
  });

  // Extract filename from Content-Disposition header, fallback to default
  const disposition = response.headers['content-disposition'] || '';
  const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : 'CAB.md';

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Download the CAB as a PDF file.
 */
export async function downloadCABPdf(projectId: string): Promise<void> {
  const response = await api.get(`${CAB_BASE}/${projectId}/pdf`, {
    responseType: 'blob',
  });

  const disposition = response.headers['content-disposition'] || '';
  const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : 'CAB.pdf';

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Get an individual section JSON.
 */
export async function getCABSection(projectId: string, sectionName: string): Promise<any> {
  const response = await api.get(`${CAB_BASE}/${projectId}/section/${sectionName}`);
  return response.data;
}

/**
 * Get cost breakdown.
 */
export async function getCABCost(projectId: string): Promise<any> {
  const response = await api.get(`${CAB_BASE}/${projectId}/cost`);
  return response.data;
}

// ════════════════════════════════════════════════════════════════════════════
// DELETE
// ════════════════════════════════════════════════════════════════════════════

/**
 * Delete all CAB data for a project.
 */
export async function deleteCAB(projectId: string): Promise<void> {
  await api.delete(`${CAB_BASE}/${projectId}`);
}
